/**
 * BlockNoteEditor.jsx — UPDATED
 *
 * Changes marked with "// 🔸 NEW" comments:
 *   1. Import TemplateVariable inline content spec
 *   2. Add it to the schema
 *   3. Import + render TemplateVariableSuggestionMenu
 *   4. New prop: `enableTemplateVariables` (default false)
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useCreateBlockNote } from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { filterSuggestionItems } from "@blocknote/core/extensions";
import { SuggestionMenuController } from "@blocknote/react";
import Box from "@mui/material/Box";

import {
  BlockNoteSchema,
  defaultInlineContentSpecs,
  defaultBlockSpecs,
  defaultStyleSpecs,
} from "@blocknote/core";
import { createReactInlineContentSpec } from "@blocknote/react";

// 🔸 NEW — import the template variable spec + suggestion menu
import { TemplateVariable } from "./templateVariables";
import TemplateVariableSuggestionMenu from "./TemplateVariableSuggestionMenu";

// ─── Mention inline content (unchanged) ─────────────────────────────
export const Mention = createReactInlineContentSpec(
  {
    type: "mention",
    propSchema: {
      user: { default: "Unknown" },
      email: { default: "" },
    },
    content: "none",
  },
  {
    render: (props) => {
      const { user, email } = props.inlineContent.props;
      return (
        <span
          style={{
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
            borderRadius: "4px",
            padding: "1px 4px",
            fontSize: "inherit",
            fontWeight: 500,
            cursor: "default",
            whiteSpace: "nowrap",
          }}
          data-mention
          data-value={email}
        >
          @{user}
        </span>
      );
    },
    toExternalHTML: (props) => {
      const { user, email } = props.inlineContent.props;
      return (
        <a
          href={user}
          className="wysiwyg-mention"
          data-mention=""
          data-value={email}
        >
          {user} &lt;{email}&gt;
        </a>
      );
    },
    parse: (element) => {
      if (
        element.className?.includes("wysiwyg-mention") ||
        element.hasAttribute("data-mention")
      ) {
        const email = element.getAttribute("data-value") || "";
        const href = element.getAttribute("href") || "";
        const user = href || element.textContent?.split("<")[0]?.trim() || "";
        return { user, email };
      }
      return undefined;
    },
  },
);

// ─── Utility ────────────────────────────────────────────────────────
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ─── Schema ─────────────────────────────────────────────────────────
// 🔸 NEW — added `templateVariable: TemplateVariable` to inlineContentSpecs
export const loPortalSchema = BlockNoteSchema.create({
  blockSpecs: {
    ...defaultBlockSpecs,
  },
  inlineContentSpecs: {
    ...defaultInlineContentSpecs,
    mention: Mention,
    templateVariable: TemplateVariable, // 🔸 NEW
  },
  styleSpecs: {
    ...defaultStyleSpecs,
  },
});

// ─── Component ──────────────────────────────────────────────────────
const BlockNoteEditor = ({
  value,
  onChange,
  readOnly = false,
  placeholder = "Start typing...",
  maxHeight = "240px",
  minHeight,
  suppressOuterBorder = false,
  borderRadius = "8px",
  backgroundColor,
  sx,
  children,
  id,
  enableMentions = true,
  enableTemplateVariables = false, // 🔸 NEW PROP
  sideMenu = true,
  formattingToolbar = true,
  slashMenu = true,
  tables = true,
  advancedTables = false,
  editorRef,
}) => {
  const users = useSelector((state) => state?.users);
  // Starts EMPTY (not `value`) so a value that's already there on mount is
  // loaded too — seeding it with `value` made the first render skip the load
  // and the editor came up blank.
  const prevValueRef = useRef(undefined);

  const tablesOption = useMemo(() => {
    if (!tables) return undefined;
    if (advancedTables) {
      return {
        splitCells: true,
        cellBackgroundColor: true,
        cellTextColor: true,
        headers: true,
      };
    }
    return undefined;
  }, [tables, advancedTables]);

  const editor = useCreateBlockNote({
    schema: loPortalSchema,
    ...(tablesOption && { tables: tablesOption }),
    domAttributes: {
      editor: {
        style: [
          `font-size: 12px`,
          `font-family: var(--primaryFont, inherit)`,
          minHeight ? `min-height: ${minHeight}` : "",
          maxHeight && maxHeight !== "unset" ? `max-height: ${maxHeight}` : "",
          maxHeight && maxHeight !== "unset" ? `overflow-y: auto` : "",
        ]
          .filter(Boolean)
          .join("; "),
      },
    },
  });

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  // ─── Mention suggestions (unchanged) ──────────────────────────────
  const getMentionItems = useCallback(
    (query) => {
      const items = (users || [])
        .map((u) => ({
          title: `${u?.fullName} <${u?.emailAddress}>`,
          onItemClick: () => {
            editor.insertInlineContent([
              {
                type: "mention",
                props: {
                  user: u.fullName,
                  email: u.emailAddress,
                },
              },
              " ",
            ]);
          },
        }))
        .sort((a, b) => a.title.localeCompare(b.title));

      return filterSuggestionItems(items, query);
    },
    [users, editor],
  );

  // ─── Load external HTML value ─────────────────────────────────────
  // The parent echoing our own onChange back is caught by the equality check
  // (handleChange records the html it emitted). A one-shot "internal change"
  // flag used to sit here too, but it was left armed whenever the parent didn't
  // echo, and then swallowed the next real value change.
  useEffect(() => {
    if (value === prevValueRef.current) return;
    prevValueRef.current = value;

    const loadHtml = async () => {
      if (typeof value === "string" && value.trim()) {
        const sanitized = value
          .replace(/<ins>/gi, "<u>")
          .replace(/<\/ins>/gi, "</u>");
        const blocks = await editor.tryParseHTMLToBlocks(sanitized);
        editor.replaceBlocks(editor.document, blocks);
      } else if (!value) {
        editor.replaceBlocks(editor.document, [
          { type: "paragraph", content: "" },
        ]);
      }
    };
    loadHtml();
  }, [value, editor]);

  // ─── Clipboard paste (unchanged) ──────────────────────────────────
  useEffect(() => {
    const editorElement = editor?.domElement;
    if (!editorElement) return;

    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          e.stopPropagation();

          const file = item.getAsFile();
          if (!file) return;

          try {
            const url = await fileToDataUrl(file);
            editor.insertBlocks(
              [
                {
                  type: "image",
                  props: {
                    url,
                    caption: file.name || "Pasted image",
                    previewWidth: 400,
                  },
                },
              ],
              editor.getTextCursorPosition().block,
              "after",
            );
          } catch (err) {
            console.error("BlockNoteEditor: failed to paste image", err);
          }
          return;
        }
      }
    };

    editorElement.addEventListener("paste", handlePaste, true);
    return () => editorElement.removeEventListener("paste", handlePaste, true);
  }, [editor, editor?.domElement]);

  // ─── onChange handler ─────────────────────────────────────────────
  const handleChange = useCallback(async () => {
    if (!onChange) return;

    const htmlString = await editor.blocksToHTMLLossy(editor.document);
    const plainText = editor.document
      .map((block) => {
        if (!block.content) return "";
        if (typeof block.content === "string") return block.content;
        if (Array.isArray(block.content)) {
          return block.content
            .map((ic) => {
              if (ic.type === "text") return ic.text;
              if (ic.type === "mention") return `@${ic.props?.user || ""}`;
              // 🔸 NEW — extract template variable as {{variable}} in plain text
              if (ic.type === "templateVariable")
                return `{{${ic.props?.variable || ""}}}`;
              if (ic.type === "link")
                return typeof ic.content === "string"
                  ? ic.content
                  : ic.content?.map((t) => t.text).join("") || "";
              return "";
            })
            .join("");
        }
        if (block.content?.type === "tableContent") {
          return block.content.rows
            .map((row) =>
              row.cells
                .map((cell) => {
                  if (Array.isArray(cell)) {
                    return cell.map((ic) => ic.text || "").join("");
                  }
                  if (cell?.content && Array.isArray(cell.content)) {
                    return cell.content.map((ic) => ic.text || "").join("");
                  }
                  return "";
                })
                .join("\t"),
            )
            .join("\n");
        }
        return "";
      })
      .join("\n");

    prevValueRef.current = htmlString;

    onChange({ htmlString, plainText, blocks: editor.document });
  }, [editor, onChange]);

  // ─── Styles ───────────────────────────────────────────────────────
  const wrapperSx = useMemo(
    () => ({
      position: "relative",
      width: "100%",
      borderRadius,
      paddingTop: 1,
      border: suppressOuterBorder ? "none" : "1px solid #dde0e4",
      backgroundColor: backgroundColor || "#fff",
      overflow: "hidden",
      ".bn-editor": {
        fontSize: "12px",
        fontFamily: "var(--primaryFont, inherit)",
      },
      ...(!sideMenu && {
        ".bn-side-menu": { display: "none" },
      }),
      ...(!formattingToolbar && {
        ".bn-toolbar": { display: "none" },
      }),
      ".bn-block-content[data-content-type='table']": {
        overflowX: "auto",
      },
      ...sx,
    }),
    [
      borderRadius,
      suppressOuterBorder,
      backgroundColor,
      sideMenu,
      formattingToolbar,
      sx,
    ],
  );

  return (
    <Box id={id} sx={wrapperSx} className="blocknote-editor-root">
      <BlockNoteView
        editor={editor}
        editable={!readOnly}
        onChange={handleChange}
        theme="light"
        sideMenu={sideMenu}
        formattingToolbar={formattingToolbar}
        slashMenu={slashMenu}
        tableHandles={tables}
      >
        {enableMentions && !readOnly && (
          <SuggestionMenuController
            triggerCharacter="@"
            getItems={async (query) => getMentionItems(query)}
          />
        )}

        {/* 🔸 NEW — Template variable suggestion menu */}
        {enableTemplateVariables && !readOnly && (
          <TemplateVariableSuggestionMenu editor={editor} />
        )}
      </BlockNoteView>
      {children}
    </Box>
  );
};

export default BlockNoteEditor;

export const getAllBlockNoteMentions = (editor) => {
  const mentions = [];
  editor.document.forEach((block) => {
    if (Array.isArray(block.content)) {
      block.content.forEach((ic) => {
        if (ic.type === "mention" && ic.props?.email) {
          mentions.push(ic.props.email);
        }
      });
    }
  });
  return mentions;
};

// /**
//  * BlockNoteEditor.jsx — UPDATED
//  *
//  * Changes marked with "// 🔸 NEW" comments:
//  *   1. Import TemplateVariable inline content spec
//  *   2. Add it to the schema
//  *   3. Import + render TemplateVariableSuggestionMenu
//  *   4. New prop: `enableTemplateVariables` (default false)
//  */
// import { useCallback, useEffect, useMemo, useRef } from "react";
// import { useSelector } from "react-redux";
// import { useCreateBlockNote } from "@blocknote/react";
// import { BlockNoteView } from "@blocknote/mantine";
// import { filterSuggestionItems } from "@blocknote/core/extensions";
// import { SuggestionMenuController } from "@blocknote/react";
// import Box from "@mui/material/Box";

// import {
//   BlockNoteSchema,
//   defaultInlineContentSpecs,
//   defaultBlockSpecs,
//   defaultStyleSpecs,
// } from "@blocknote/core";
// import { createReactInlineContentSpec } from "@blocknote/react";

// // 🔸 NEW — import the template variable spec + suggestion menu
// import { TemplateVariable } from "./templateVariables";
// import TemplateVariableSuggestionMenu from "./TemplateVariableSuggestionMenu";

// // ─── Mention inline content (unchanged) ─────────────────────────────
// export const Mention = createReactInlineContentSpec(
//   {
//     type: "mention",
//     propSchema: {
//       user: { default: "Unknown" },
//       email: { default: "" },
//     },
//     content: "none",
//   },
//   {
//     render: (props) => {
//       const { user, email } = props.inlineContent.props;
//       return (
//         <span
//           style={{
//             backgroundColor: "#e3f2fd",
//             color: "#1565c0",
//             borderRadius: "4px",
//             padding: "1px 4px",
//             fontSize: "inherit",
//             fontWeight: 500,
//             cursor: "default",
//             whiteSpace: "nowrap",
//           }}
//           data-mention
//           data-value={email}
//         >
//           @{user}
//         </span>
//       );
//     },
//     toExternalHTML: (props) => {
//       const { user, email } = props.inlineContent.props;
//       return (
//         <a
//           href={user}
//           className="wysiwyg-mention"
//           data-mention=""
//           data-value={email}
//         >
//           {user} &lt;{email}&gt;
//         </a>
//       );
//     },
//     parse: (element) => {
//       if (
//         element.className?.includes("wysiwyg-mention") ||
//         element.hasAttribute("data-mention")
//       ) {
//         const email = element.getAttribute("data-value") || "";
//         const href = element.getAttribute("href") || "";
//         const user = href || element.textContent?.split("<")[0]?.trim() || "";
//         return { user, email };
//       }
//       return undefined;
//     },
//   },
// );

// // ─── Utility ────────────────────────────────────────────────────────
// const fileToDataUrl = (file) =>
//   new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.onload = () => resolve(reader.result);
//     reader.onerror = reject;
//     reader.readAsDataURL(file);
//   });

// // ─── Schema ─────────────────────────────────────────────────────────
// // 🔸 NEW — added `templateVariable: TemplateVariable` to inlineContentSpecs
// export const loPortalSchema = BlockNoteSchema.create({
//   blockSpecs: {
//     ...defaultBlockSpecs,
//   },
//   inlineContentSpecs: {
//     ...defaultInlineContentSpecs,
//     mention: Mention,
//     templateVariable: TemplateVariable, // 🔸 NEW
//   },
//   styleSpecs: {
//     ...defaultStyleSpecs,
//   },
// });

// // ─── Component ──────────────────────────────────────────────────────
// const BlockNoteEditor = ({
//   value,
//   onChange,
//   readOnly = false,
//   placeholder = "Start typing...",
//   maxHeight = "240px",
//   minHeight,
//   suppressOuterBorder = false,
//   borderRadius = "8px",
//   backgroundColor,
//   sx,
//   children,
//   id,
//   enableMentions = true,
//   enableTemplateVariables = false, // 🔸 NEW PROP
//   sideMenu = true,
//   formattingToolbar = true,
//   slashMenu = true,
//   tables = true,
//   advancedTables = false,
//   editorRef,
// }) => {
//   const users = useSelector((state) => state?.users);
//   const prevValueRef = useRef(value);
//   const isInternalChange = useRef(false);

//   const tablesOption = useMemo(() => {
//     if (!tables) return undefined;
//     if (advancedTables) {
//       return {
//         splitCells: true,
//         cellBackgroundColor: true,
//         cellTextColor: true,
//         headers: true,
//       };
//     }
//     return undefined;
//   }, [tables, advancedTables]);

//   const editor = useCreateBlockNote({
//     schema: loPortalSchema,
//     ...(tablesOption && { tables: tablesOption }),
//     domAttributes: {
//       editor: {
//         style: [
//           `font-size: 12px`,
//           `font-family: var(--primaryFont, inherit)`,
//           minHeight ? `min-height: ${minHeight}` : "",
//           maxHeight && maxHeight !== "unset" ? `max-height: ${maxHeight}` : "",
//           maxHeight && maxHeight !== "unset" ? `overflow-y: auto` : "",
//         ]
//           .filter(Boolean)
//           .join("; "),
//       },
//     },
//   });

//   useEffect(() => {
//     if (editorRef) {
//       editorRef.current = editor;
//     }
//   }, [editor, editorRef]);

//   // ─── Mention suggestions (unchanged) ──────────────────────────────
//   const getMentionItems = useCallback(
//     (query) => {
//       const items = (users || [])
//         .map((u) => ({
//           title: `${u?.fullName} <${u?.emailAddress}>`,
//           onItemClick: () => {
//             editor.insertInlineContent([
//               {
//                 type: "mention",
//                 props: {
//                   user: u.fullName,
//                   email: u.emailAddress,
//                 },
//               },
//               " ",
//             ]);
//           },
//         }))
//         .sort((a, b) => a.title.localeCompare(b.title));

//       return filterSuggestionItems(items, query);
//     },
//     [users, editor],
//   );

//   // ─── Load external HTML value ─────────────────────────────────────
//   useEffect(() => {
//     if (isInternalChange.current) {
//       isInternalChange.current = false;
//       return;
//     }

//     if (value === prevValueRef.current) return;
//     prevValueRef.current = value;

//     const loadHtml = async () => {
//       if (typeof value === "string" && value.trim()) {
//         const sanitized = value
//           .replace(/<ins>/gi, "<u>")
//           .replace(/<\/ins>/gi, "</u>");
//         const blocks = await editor.tryParseHTMLToBlocks(sanitized);
//         editor.replaceBlocks(editor.document, blocks);
//       } else if (!value) {
//         editor.replaceBlocks(editor.document, [
//           { type: "paragraph", content: "" },
//         ]);
//       }
//     };
//     loadHtml();
//   }, [value, editor]);

//   // ─── Clipboard paste (unchanged) ──────────────────────────────────
//   useEffect(() => {
//     const editorElement = editor?.domElement;
//     if (!editorElement) return;

//     const handlePaste = async (e) => {
//       const items = e.clipboardData?.items;
//       if (!items) return;

//       for (const item of items) {
//         if (item.type.startsWith("image/")) {
//           e.preventDefault();
//           e.stopPropagation();

//           const file = item.getAsFile();
//           if (!file) return;

//           try {
//             const url = await fileToDataUrl(file);
//             editor.insertBlocks(
//               [
//                 {
//                   type: "image",
//                   props: {
//                     url,
//                     caption: file.name || "Pasted image",
//                     previewWidth: 400,
//                   },
//                 },
//               ],
//               editor.getTextCursorPosition().block,
//               "after",
//             );
//           } catch (err) {
//             console.error("BlockNoteEditor: failed to paste image", err);
//           }
//           return;
//         }
//       }
//     };

//     editorElement.addEventListener("paste", handlePaste, true);
//     return () => editorElement.removeEventListener("paste", handlePaste, true);
//   }, [editor, editor?.domElement]);

//   // ─── onChange handler ─────────────────────────────────────────────
//   const handleChange = useCallback(async () => {
//     if (!onChange) return;

//     const htmlString = await editor.blocksToHTMLLossy(editor.document);
//     const plainText = editor.document
//       .map((block) => {
//         if (!block.content) return "";
//         if (typeof block.content === "string") return block.content;
//         if (Array.isArray(block.content)) {
//           return block.content
//             .map((ic) => {
//               if (ic.type === "text") return ic.text;
//               if (ic.type === "mention") return `@${ic.props?.user || ""}`;
//               // 🔸 NEW — extract template variable as {{variable}} in plain text
//               if (ic.type === "templateVariable")
//                 return `{{${ic.props?.variable || ""}}}`;
//               if (ic.type === "link")
//                 return typeof ic.content === "string"
//                   ? ic.content
//                   : ic.content?.map((t) => t.text).join("") || "";
//               return "";
//             })
//             .join("");
//         }
//         if (block.content?.type === "tableContent") {
//           return block.content.rows
//             .map((row) =>
//               row.cells
//                 .map((cell) => {
//                   if (Array.isArray(cell)) {
//                     return cell.map((ic) => ic.text || "").join("");
//                   }
//                   if (cell?.content && Array.isArray(cell.content)) {
//                     return cell.content.map((ic) => ic.text || "").join("");
//                   }
//                   return "";
//                 })
//                 .join("\t"),
//             )
//             .join("\n");
//         }
//         return "";
//       })
//       .join("\n");

//     isInternalChange.current = true;
//     prevValueRef.current = htmlString;

//     onChange({ htmlString, plainText, blocks: editor.document });
//   }, [editor, onChange]);

//   // ─── Styles ───────────────────────────────────────────────────────
//   const wrapperSx = useMemo(
//     () => ({
//       position: "relative",
//       width: "100%",
//       borderRadius,
//       paddingTop: 1,
//       border: suppressOuterBorder ? "none" : "1px solid #dde0e4",
//       backgroundColor: backgroundColor || "#fff",
//       overflow: "hidden",
//       ".bn-editor": {
//         fontSize: "12px",
//         fontFamily: "var(--primaryFont, inherit)",
//       },
//       ...(!sideMenu && {
//         ".bn-side-menu": { display: "none" },
//       }),
//       ...(!formattingToolbar && {
//         ".bn-toolbar": { display: "none" },
//       }),
//       ".bn-block-content[data-content-type='table']": {
//         overflowX: "auto",
//       },
//       ...sx,
//     }),
//     [
//       borderRadius,
//       suppressOuterBorder,
//       backgroundColor,
//       sideMenu,
//       formattingToolbar,
//       sx,
//     ],
//   );

//   return (
//     <Box id={id} sx={wrapperSx} className="blocknote-editor-root">
//       <BlockNoteView
//         editor={editor}
//         editable={!readOnly}
//         onChange={handleChange}
//         theme="light"
//         sideMenu={sideMenu}
//         formattingToolbar={formattingToolbar}
//         slashMenu={slashMenu}
//         tableHandles={tables}
//       >
//         {enableMentions && !readOnly && (
//           <SuggestionMenuController
//             triggerCharacter="@"
//             getItems={async (query) => getMentionItems(query)}
//           />
//         )}

//         {/* 🔸 NEW — Template variable suggestion menu */}
//         {enableTemplateVariables && !readOnly && (
//           <TemplateVariableSuggestionMenu editor={editor} />
//         )}
//       </BlockNoteView>
//       {children}
//     </Box>
//   );
// };

// export default BlockNoteEditor;

// export const getAllBlockNoteMentions = (editor) => {
//   const mentions = [];
//   editor.document.forEach((block) => {
//     if (Array.isArray(block.content)) {
//       block.content.forEach((ic) => {
//         if (ic.type === "mention" && ic.props?.email) {
//           mentions.push(ic.props.email);
//         }
//       });
//     }
//   });
//   return mentions;
// };
